import { FC, useRef, useCallback, useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Paintbrush } from 'lucide-react';

import { Figure, PlacedFigure, getFigureSize } from './types';
import { FigureRenderer } from './FigureRenderer';

const DRAG_TYPE_SHOWCASE = 'SHOWCASE_FIGURE';
const DRAG_TYPE_PLACED = 'PLACED_FIGURE';

const PLACED_SCALE = 2;

interface ShowcaseFigureProps {
  figure: Figure;
}

const ShowcaseFigure: FC<ShowcaseFigureProps> = ({ figure }) => {
  const size = getFigureSize(figure.size);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: DRAG_TYPE_SHOWCASE,
    item: { figure },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }));

  return (
    <div
      ref={drag}
      className="cursor-grab active:cursor-grabbing flex items-center justify-center p-1 rounded hover:bg-white/20 transition-colors"
      style={{ opacity: isDragging ? 0.4 : 1, width: size + 8, height: size + 8 }}
      title={figure.name}
    >
      <FigureRenderer figure={figure} className="w-full h-full" />
    </div>
  );
};

interface PlacedFigureItemProps {
  figure: PlacedFigure;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, scale: number) => void;
  onHover: (id: string | null) => void;
  boardRef: React.RefObject<HTMLDivElement | null>;
}

const PlacedFigureItem: FC<PlacedFigureItemProps> = ({ figure, onMove, onResize, onHover, boardRef }) => {
  const size = Math.round(getFigureSize(figure.size) * PLACED_SCALE * (figure.scale ?? 1));
  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);

  useEffect(
    () => () => {
      isResizingRef.current = false;
    },
    [],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DRAG_TYPE_PLACED,
      item: { figure },
      canDrag: () => !isResizingRef.current,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [figure],
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, deltaFn: (dx: number, dy: number) => number) => {
      e.stopPropagation();
      e.preventDefault();

      const startX = e.clientX;
      const startY = e.clientY;
      const startScale = figure.scale ?? 1;

      isResizingRef.current = true;
      setIsResizing(true);

      const handleMouseMove = (ev: MouseEvent) => {
        const delta = deltaFn(ev.clientX - startX, ev.clientY - startY);
        onResize(figure.id, Math.max(0.3, Math.min(3, startScale + delta)));
      };

      const handleMouseUp = () => {
        isResizingRef.current = false;
        setIsResizing(false);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [figure.id, figure.scale, onResize],
  );

  const resizeCorners = [
    { cls: 'top-1 left-1',     cursor: 'nwse-resize', delta: (dx: number, dy: number) => (-dx - dy) / 200 },
    { cls: 'top-1 right-1',    cursor: 'nesw-resize', delta: (dx: number, dy: number) => (dx - dy) / 200 },
    { cls: 'bottom-1 left-1',  cursor: 'nesw-resize', delta: (dx: number, dy: number) => (-dx + dy) / 200 },
    { cls: 'bottom-1 right-1', cursor: 'nwse-resize', delta: (dx: number, dy: number) => (dx + dy) / 200 },
  ];

  return (
    <div
      ref={drag}
      className="absolute cursor-grab active:cursor-grabbing"
      style={{
        left: `${figure.x}%`,
        top: `${figure.y}%`,
        transform: `translate(-50%, -50%) rotate(${figure.rotation}deg)`,
        width: size,
        height: size,
        opacity: isDragging ? 0.3 : 1,
        zIndex: isDragging || isResizing ? 1000 : 10,
      }}
      onMouseEnter={() => {
        setIsHovered(true);
        onHover(figure.id);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onHover(null);
      }}
    >
      <FigureRenderer figure={figure} className="w-full h-full" />

      {(isHovered || isResizing) &&
        resizeCorners.map(({ cls, cursor, delta }) => (
          <div
            key={cls}
            className={`absolute ${cls} w-3.5 h-3.5 rounded-full bg-white/90 border-2 border-gray-400 shadow-md`}
            style={{ cursor, zIndex: 20 }}
            onMouseDown={(e) => handleResizeMouseDown(e, delta)}
            onDragStart={(e) => e.preventDefault()}
          />
        ))}
    </div>
  );
};

interface BoardDropZoneProps {
  placedFigures: PlacedFigure[];
  onDrop: (figure: Figure, x: number, y: number) => void;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, scale: number) => void;
  onRotate: (id: string) => void;
  onRemove: (id: string) => void;
  sandColor: string;
  onSandColorChange?: (color: string) => void;
  readonly?: boolean;
}

const BoardDropZone: FC<BoardDropZoneProps> = ({
  placedFigures,
  onDrop,
  onMove,
  onResize,
  onRotate,
  onRemove,
  sandColor,
  onSandColorChange,
  readonly = false,
}) => {
  const boardRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [hoveredFigureId, setHoveredFigureId] = useState<string | null>(null);

  const getRelativePos = useCallback((clientX: number, clientY: number) => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return { x: 50, y: 50 };
    const x = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.max(5, Math.min(95, ((clientY - rect.top) / rect.height) * 100));
    return { x, y };
  }, []);

  const [{ isOver }, drop] = useDrop<{ figure: Figure } | { figure: PlacedFigure }, void, { isOver: boolean }>(
    () => ({
      accept: [DRAG_TYPE_SHOWCASE, DRAG_TYPE_PLACED],
      drop: (item, monitor) => {
        if (readonly) return;
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const { x, y } = getRelativePos(clientOffset.x, clientOffset.y);
        const f = item.figure;
        if ('x' in f) {
          onMove(f.id, x, y);
        } else {
          onDrop(f, x, y);
        }
      },
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [getRelativePos, onDrop, onMove, readonly],
  );

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      (boardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      drop(node);
    },
    [drop],
  );

  return (
    <div
      ref={setRef}
      className="relative w-full h-full rounded-lg overflow-hidden"
      style={{
        border: isOver ? '2px solid rgba(255,255,255,0.6)' : '2px solid transparent',
        backgroundImage: `
          radial-gradient(ellipse at 30% 40%, ${sandColor}ee, ${sandColor}aa),
          repeating-linear-gradient(45deg, rgba(0,0,0,0.03) 0px, rgba(0,0,0,0.03) 1px, transparent 1px, transparent 8px)
        `,
      }}
    >
      {placedFigures.length === 0 && !readonly && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <svg className="w-12 h-12 mb-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
            />
          </svg>
          <p className="text-gray-600 text-sm font-comfortaa text-center">Перетащите фигуры сюда</p>
        </div>
      )}

      {placedFigures.map((fig) =>
        readonly ? (
          <div
            key={fig.id}
            className="absolute"
            style={{
              left: `${fig.x}%`,
              top: `${fig.y}%`,
              transform: `translate(-50%, -50%) rotate(${fig.rotation}deg)`,
              width: Math.round(getFigureSize(fig.size) * PLACED_SCALE * (fig.scale ?? 1)),
              height: Math.round(getFigureSize(fig.size) * PLACED_SCALE * (fig.scale ?? 1)),
            }}
          >
            <FigureRenderer figure={fig} className="w-full h-full" />
          </div>
        ) : (
          <PlacedFigureItem
            key={fig.id}
            figure={fig}
            onMove={onMove}
            onResize={onResize}
            onHover={setHoveredFigureId}
            boardRef={boardRef}
          />
        ),
      )}

      {!readonly &&
        (() => {
          const fig = placedFigures.find((f) => f.id === hoveredFigureId);
          if (!fig) return null;
          return (
            <div
              className="absolute z-50 flex gap-1 pointer-events-none"
              style={{
                left: `${fig.x}%`,
                top: `${fig.y}%`,
                transform: `translate(-50%, calc(-50% - ${Math.round(getFigureSize(fig.size) * PLACED_SCALE * (fig.scale ?? 1)) / 2 + 8}px))`,
              }}
              onMouseEnter={() => setHoveredFigureId(fig.id)}
              onMouseLeave={() => setHoveredFigureId(null)}
            >
              <button
                className="pointer-events-auto bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors"
                title="Повернуть"
                onClick={(e) => {
                  e.stopPropagation();
                  onRotate(fig.id);
                }}
              >
                ↻
              </button>
              <button
                className="pointer-events-auto bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                title="Удалить"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(fig.id);
                  setHoveredFigureId(null);
                }}
              >
                ✕
              </button>
            </div>
          );
        })()}

      {onSandColorChange && !readonly && (
        <div className="absolute top-3 right-3 z-30">
          <input
            ref={colorInputRef}
            type="color"
            value={sandColor}
            onChange={(e) => onSandColorChange(e.target.value)}
            className="absolute opacity-0 w-0 h-0 pointer-events-none"
            tabIndex={-1}
          />
          <div
            className="w-12 h-12 bg-white/40 hover:bg-white/60 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer transition-all shadow-lg hover:shadow-xl group"
            onClick={(e) => {
              e.stopPropagation();
              colorInputRef.current?.click();
            }}
            title="Изменить цвет песка"
          >
            <Paintbrush className="w-6 h-6 text-gray-700 group-hover:text-gray-900 transition-colors" strokeWidth={2} />
          </div>
        </div>
      )}
    </div>
  );
};

export interface SandTherapyBoardProps {
  selectedFigures: Figure[];
  placedFigures: PlacedFigure[];
  onPlaceFigure: (figure: PlacedFigure) => void;
  onMoveFigure: (id: string, x: number, y: number) => void;
  onRotateFigure: (id: string) => void;
  onResizeFigure: (id: string, scale: number) => void;
  onRemoveFigure: (id: string) => void;
  onSandColorChange?: (color: string) => void;
  onClientReady?: () => void;
  sandColor?: string;
  clientReady?: boolean;
  readonly?: boolean;
}

const SandTherapyBoardInner: FC<SandTherapyBoardProps> = ({
  selectedFigures,
  placedFigures,
  onPlaceFigure,
  onMoveFigure,
  onRotateFigure,
  onResizeFigure,
  onRemoveFigure,
  onSandColorChange,
  onClientReady,
  sandColor = '#fef3c7',
  clientReady = false,
  readonly = false,
}) => {
  const handleDrop = useCallback(
    (figure: Figure, x: number, y: number) => {
      const placed: PlacedFigure = {
        ...figure,
        id: `${figure.id}-placed-${Date.now()}-${Math.random()}`,
        x,
        y,
        rotation: 0,
        scale: 1,
      };
      onPlaceFigure(placed);
    },
    [onPlaceFigure],
  );

  return (
    <div className="flex w-[1200px] min-w-[1200px] flex-col h-full gap-3">
      {!readonly && (
        <div className="flex-shrink-0">
          <p className="text-white/70 text-xs font-comfortaa mb-1">Доступные фигуры</p>
          <div className="bg-white/10 rounded-lg p-2 overflow-x-auto">
            <div className="flex flex-wrap gap-1 min-h-[56px]">
              {selectedFigures.map((fig) => (
                <ShowcaseFigure key={fig.id} figure={fig} />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0">
        <BoardDropZone
          placedFigures={placedFigures}
          onDrop={handleDrop}
          onMove={onMoveFigure}
          onResize={onResizeFigure}
          onRotate={onRotateFigure}
          onRemove={onRemoveFigure}
          sandColor={sandColor}
          onSandColorChange={onSandColorChange}
          readonly={readonly}
        />
      </div>

      {!readonly && (
        <div className="flex-shrink-0 flex items-center justify-center gap-3">
          {onClientReady && (
            <button
              onClick={clientReady ? undefined : onClientReady}
              disabled={clientReady}
              className={
                clientReady
                  ? 'px-30 py-2 rounded-[10px] bg-gray-300 text-gray-500 font-comfortaa font-bold cursor-not-allowed'
                  : 'px-30 py-2 rounded-[10px] bg-[#C00000] text-white font-comfortaa font-bold hover:bg-[#a00000] transition-colors cursor-pointer'
              }
            >
              {clientReady ? 'Готово ✓' : 'Готово'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const SandTherapyBoard: FC<SandTherapyBoardProps> = (props) => (
  <DndProvider backend={HTML5Backend}>
    <SandTherapyBoardInner {...props} />
  </DndProvider>
);
